import { useEventTracking } from '@services/statsig';
import { useSnackbar } from 'notistack';

/**
 * Valid source values for referral link copy analytics
 */
export type ReferralCopySource =
  | 'dashboard_header'
  | 'referral_dashboard'
  | 'csat_modal'
  | 'how_referrals_modal';

/**
 * Generate referral signup link from code
 */
export const getReferralLink = (code: string): string => {
  const baseUrl = import.meta.env.VITE_MAIN_URL;
  if (!baseUrl) {
    console.warn('VITE_MAIN_URL not set, using current origin for referral link');
  }
  return `${baseUrl || window.location.origin}/signup?ref=${code}`;
};

/**
 * Hook to handle copying referral links with consistent:
 * - Clipboard functionality
 * - Snackbar notifications
 * - Analytics tracking
 *
 * @returns copyLink function and getReferralLink utility
 */
export function useCopyReferralLink() {
  const { enqueueSnackbar } = useSnackbar();
  const { referral } = useEventTracking();

  const copyLink = async (code: string, source: ReferralCopySource): Promise<boolean> => {
    const link = getReferralLink(code);
    try {
      await navigator.clipboard.writeText(link);
      enqueueSnackbar('Referral link copied!', { variant: 'success' });
      referral.linkCopied({ referral_code: code, source });
      return true;
    } catch (err) {
      referral.linkCopyFailed({
        error_message: err instanceof Error ? err.message : 'Unknown error',
      });
      enqueueSnackbar('Failed to copy link', { variant: 'error' });
      return false;
    }
  };

  return { copyLink, getReferralLink };
}
